/**
 * 勘定科目の一括取り込み(2026-10-23 発注者指示「Gate と v8 の違いを、取り急ぎ変更できる仕様に」)。
 * Excel「Gate と v8 の勘定科目の違い」の「取り込み用」シートを貼り付け、科目①・科目②をまとめて追加する。
 *
 *  - 見出しの列名で読む(順番は自由): 区分 / 科目① / コード / 5行PLの行 / 経費申請 / 科目② / 説明
 *  - 科目①の ID は「区分-科目①」(既存の v2 と同じ形)。同じ区分に同じ科目①があれば追加しない(科目②だけ足す)。
 *    同じかどうかは予算の取り込みと同じ matchAccount(名前 → コード・略称。v8 の「仕入」=仕入高・販管の「給料手当」=給与手当 も同じとみなす)
 *  - 科目②は「・」区切り。その科目①に同じ名前の科目②が無いものだけ追加する
 *  - 既存の科目の名前・区分・PL行は変えない(変えるときは科目①の編集で)
 * 書き込みは画面(AccountsTab)が masterRepo の upsertAccount / upsertSubAccount で行う。ここは読み取りと計画だけ
 */
import { matchAccount } from '@core/budget/budgetImport';
import type { Account, AccountCategory, DepartmentId, PlLine, SubAccount } from '@core/lib/types';

/** 区分(項目)。特利・特損は 2027年度予算 v8 の区分(2026-10-23 追加) */
export const ITEM_CATEGORIES = ['売上', '原価', '販管', '外収', '外費', '特利', '特損', '資産', '負債'] as const;

export const IMPORT_HEADERS = ['区分', '科目①', 'コード', '5行PLの行', '経費申請', '科目②', '説明'] as const;

const CATEGORY_OF: Record<string, AccountCategory> = {
  売上: 'revenue',
  外収: 'revenue',
  特利: 'revenue',
  原価: 'cogs',
  販管: 'sgna',
  外費: 'sgna',
  特損: 'sgna',
  資産: 'asset',
  負債: 'liability',
};

/** 5行PLの行の既定(外収・特利は売上、外費・特損は経費。既存の雑収入・支払利息と同じ扱い) */
const PL_LINE_OF: Record<string, PlLine> = {
  売上: 'revenue',
  外収: 'revenue',
  特利: 'revenue',
  原価: 'cogs',
  販管: 'expense',
  外費: 'expense',
  特損: 'expense',
  資産: null,
  負債: null,
};

const PL_LINE_WORDS: Record<string, PlLine> = { 売上: 'revenue', 原価: 'cogs', 経費: 'expense', 対象外: null };

/** 経費申請で選べるかの既定(原価・販管・外費は選べる) */
const SELECTABLE_DEFAULT = new Set(['原価', '販管', '外費']);

export type AccountImportRow = {
  line: number;
  status: 'new' | 'exists' | 'error';
  message: string;
  itemCategory: string;
  name: string;
  /** 追加する科目①(status=new のとき) */
  account: Account | null;
  /** 追加する科目②(新規の科目①・既存の科目①のどちらにも) */
  subAccounts: SubAccount[];
};

const norm = (s: string) => s.replace(/[\s　]/g, '');

function splitSubs(s: string): string[] {
  return s
    .split(/[・、,／/\n]/)
    .map((x) => x.trim())
    .filter(Boolean);
}

export function checkAccountImport(params: {
  table: string[][];
  accounts: Account[];
  subAccounts: SubAccount[];
  /** 経費申請で選べる科目に入れる事業部(全事業部) */
  allDepartmentIds: DepartmentId[];
}): { rows: AccountImportRow[]; error: string | null } {
  const { table, accounts, subAccounts, allDepartmentIds } = params;
  const headerIndex = table.findIndex((r) => r.some((c) => norm(c) === '区分') && r.some((c) => norm(c) === '科目①'));
  if (headerIndex < 0) return { rows: [], error: '見出しの行(「区分」「科目①」を含む行)が見つかりません' };
  const head = table[headerIndex]!.map(norm);
  const col = (name: string) => head.indexOf(norm(name));
  const cell = (r: string[], name: string) => {
    const i = col(name);
    return i >= 0 ? (r[i] ?? '').trim() : '';
  };

  const rows: AccountImportRow[] = [];
  // 新しい科目①の表示順: 同じ区分の最後の後ろ(無ければ全体の最後の後ろ)に10刻みで
  const maxOrder = (cat: string) => {
    const same = accounts.filter((a) => (a.item_category ?? '') === cat).map((a) => a.display_order);
    return same.length ? Math.max(...same) : Math.max(0, ...accounts.map((a) => a.display_order));
  };
  const added = new Map<string, number>();
  const plannedIds = new Set<string>();
  // 科目②のコードの連番(科目①のコード + 2桁)
  const subCount = new Map<string, number>();
  for (const s of subAccounts) subCount.set(s.account_id, (subCount.get(s.account_id) ?? 0) + 1);

  table.slice(headerIndex + 1).forEach((r, i) => {
    const line = headerIndex + 2 + i;
    if (r.every((c) => !c.trim())) return;
    const itemCategory = cell(r, '区分');
    const name = cell(r, '科目①');
    const base = { line, itemCategory, name, account: null, subAccounts: [] as SubAccount[] };
    if (!itemCategory && !name) return;
    if (!(ITEM_CATEGORIES as readonly string[]).includes(itemCategory)) {
      rows.push({ ...base, status: 'error', message: `区分は ${ITEM_CATEGORIES.join('・')} のどれかです` });
      return;
    }
    if (!name) {
      rows.push({ ...base, status: 'error', message: '科目①が空です' });
      return;
    }
    if (name.includes('/')) {
      rows.push({ ...base, status: 'error', message: '科目①に「/」は使えません' });
      return;
    }
    const plWord = cell(r, '5行PLの行');
    if (plWord && !(plWord in PL_LINE_WORDS)) {
      rows.push({ ...base, status: 'error', message: '5行PLの行は 売上・原価・経費・対象外 のどれかです' });
      return;
    }
    const selWord = cell(r, '経費申請');
    if (selWord && !['○', '×', '〇'].includes(selWord)) {
      rows.push({ ...base, status: 'error', message: '経費申請は ○ か × です' });
      return;
    }

    const matchedId = matchAccount(accounts, itemCategory, name);
    const existing = matchedId ? accounts.find((a) => a.id === matchedId) : undefined;
    const id = existing?.id ?? `${itemCategory}-${name}`;
    if (!existing && (plannedIds.has(id) || accounts.some((a) => a.id === id))) {
      rows.push({ ...base, status: 'error', message: `同じ ID(${id})の科目①が既にあるか、上の行と重複しています` });
      return;
    }
    const code = cell(r, 'コード') || existing?.code || name.slice(0, 3);

    let account: Account | null = null;
    if (!existing) {
      plannedIds.add(id);
      const n = (added.get(itemCategory) ?? 0) + 1;
      added.set(itemCategory, n);
      const selectable = selWord ? selWord !== '×' : SELECTABLE_DEFAULT.has(itemCategory);
      account = {
        id,
        code,
        name,
        category: CATEGORY_OF[itemCategory] ?? 'sgna',
        pl_line: plWord ? (PL_LINE_WORDS[plWord] ?? null) : (PL_LINE_OF[itemCategory] ?? null),
        expense_selectable: selectable,
        item_category: itemCategory,
        abbr: code,
        description: cell(r, '説明') || '',
        // 経費申請の表示は「事業部の選択」で決まる(空=出さない)
        department_ids: selectable ? allDepartmentIds : [],
        display_order: maxOrder(itemCategory) + n * 10,
        active: true,
      };
    }

    const have = new Set(subAccounts.filter((s) => s.account_id === id).map((s) => norm(s.name)));
    const subs: SubAccount[] = [];
    for (const subName of splitSubs(cell(r, '科目②'))) {
      if (have.has(norm(subName))) continue;
      have.add(norm(subName));
      const k = (subCount.get(id) ?? 0) + 1;
      subCount.set(id, k);
      subs.push({
        id: `${id}-${subName}`,
        account_id: id,
        code: `${code}-${String(k).padStart(2, '0')}`,
        name: subName,
        display_order: k,
        active: true,
      });
    }

    if (existing) {
      rows.push({
        ...base,
        status: subs.length ? 'new' : 'exists',
        message: subs.length ? `科目①は既にあります。科目②を${subs.length}件追加します` : '既にあります(追加するものはありません)',
        subAccounts: subs,
      });
    } else {
      rows.push({
        ...base,
        status: 'new',
        message: `科目①を追加${subs.length ? `・科目②${subs.length}件` : ''}(5行PL: ${
          account?.pl_line === 'revenue' ? '売上' : account?.pl_line === 'cogs' ? '原価' : account?.pl_line === 'expense' ? '経費' : '対象外'
        }・経費申請: ${account?.expense_selectable ? '○' : '×'})`,
        account,
        subAccounts: subs,
      });
    }
  });

  return { rows, error: rows.length === 0 ? '取り込む行がありません' : null };
}
